import { useCallback } from 'react'
import { AnyAction } from 'redux'
import { useDispatch } from 'react-redux'
import { Required } from 'utility-types'

import { DispatchReturnValue } from '@cbgms/base/redux/async-action'
import { createAsyncApiAction } from '@cbgms/base/redux/create-async-action'

import {
  Application,
  ExportData,
  ListDataResponse,
  ListParams,
  ListSettings,
  ListSettingsResponse,
  LoadData,
  LoadSettings,
  RestoreDefaultSettings,
  SaveSettings
} from './types'

function loadListSettings(application: Application, path: string) {
  return createAsyncApiAction<ListSettingsResponse>(application, {
    method: 'GET',
    url: `/settings/global/list/${path}`
  })
}

function saveListSettings(application: Application, path: string, data: Partial<ListSettings>) {
  return createAsyncApiAction<void>(application, {
    method: 'POST',
    url: `/settings/global/list/${path}`,
    data
  })
}

function restoreListSettingsDefaults(application: Application, path: string) {
  return createAsyncApiAction<void>(application, {
    method: 'DELETE',
    url: `/settings/global/list/${path}`
  })
}

function loadListData<T>(application: Application, path: string, params: Omit<ListParams, 'format'>) {
  return createAsyncApiAction<ListDataResponse<T>>(application, {
    method: 'GET',
    url: `/modules/${path}`,
    params: {
      Fields: params.fields,
      OrderBy: params.orderBy,
      Page: (params.page ?? 0) + 1,
      PerPage: params.perPage,
      Search: params.search
    }
  })
}

function exportListData(application: Application, path: string, params: Required<ListParams, 'format'>) {
  return createAsyncApiAction<Blob>(application, {
    method: 'GET',
    url: `/modules/${path}`,
    params: {
      Fields: params.fields,
      OrderBy: params.orderBy,
      Page: (params.page ?? 0) + 1,
      PerPage: params.perPage,
      Search: params.search,
      Format: params.format
    }
  })
}

interface UseListServiceOptions {
  application: Application
  module: string
  setting: string
}

type AsyncDispatch = <T extends AnyAction>(action: T) => DispatchReturnValue<T>

export interface UseListService<T> {
  exportCSV: ExportData
  exportExcel: ExportData
  loadData: LoadData<T>
  loadSettings: LoadSettings
  restoreDefaultSettings: RestoreDefaultSettings
  saveSettings: SaveSettings
}

export function useListService<T>(options: UseListServiceOptions): UseListService<T> {
  const dispatch = useDispatch<AsyncDispatch>()

  const loadSettings = useCallback<LoadSettings>(async () => {
    return dispatch(loadListSettings(options.application, options.setting))
  }, [options.application, options.setting])

  const saveSettings = useCallback<SaveSettings>(
    async data => {
      return dispatch(saveListSettings(options.application, options.setting, data))
    },
    [options.application, options.setting]
  )

  const restoreDefaultSettings = useCallback<RestoreDefaultSettings>(async () => {
    return dispatch(restoreListSettingsDefaults(options.application, options.setting))
  }, [options.application, options.setting])

  const loadData = useCallback<LoadData<T>>(
    async params => {
      return dispatch(loadListData<T>(options.application, options.module, params))
    },
    [options.application, options.module]
  )

  const exportCSV = useCallback<ExportData>(
    async params => {
      return dispatch(exportListData(options.application, options.module, { perPage: Number.MAX_SAFE_INTEGER, ...params, format: 'csv' }))
    },
    [options.application, options.module]
  )

  const exportExcel = useCallback<ExportData>(
    async params => {
      return dispatch(exportListData(options.application, options.module, { perPage: Number.MAX_SAFE_INTEGER, ...params, format: 'xlsx' }))
    },
    [options.application, options.module]
  )

  return {
    exportCSV,
    exportExcel,
    loadData,
    loadSettings,
    restoreDefaultSettings,
    saveSettings
  }
}
